import React from 'react';
import { useUserPresence } from '@/hooks/useUserPresence';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface OnlineStatusIndicatorProps {
  userId: string | undefined;
  showLabel?: boolean;
  showLastSeen?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function OnlineStatusIndicator({
  userId,
  showLabel = false,
  showLastSeen = true,
  size = 'md',
  className
}: OnlineStatusIndicatorProps) {
  const { t } = useTranslation();
  const presence = useUserPresence(userId);

  const sizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  const statusColors = {
    online: 'bg-green-500',
    away: 'bg-yellow-500',
    offline: 'bg-gray-400'
  };

  const statusLabels = {
    online: 'Online',
    away: 'Away',
    offline: 'Offline'
  };

  const getLastSeenText = () => {
    if (!presence.lastSeen) return `${t('messages.lastSeen')}: Unknown`;
    
    if (presence.isOnline) return t('messages.online');
    
    try {
      const timeAgo = formatDistanceToNow(presence.lastSeen, { addSuffix: false });
      
      // Check if it contains minutes or hours
      if (timeAgo.includes('minute')) {
        return `${t('messages.lastSeen')} ${t('messages.aboutMinutesAgo')}`;
      } else if (timeAgo.includes('hour')) {
        return `${t('messages.lastSeen')} ${t('messages.aboutHoursAgo')}`;
      }
      
      // For other cases, use the original format
      return `${t('messages.lastSeen')} ${timeAgo}`;
    } catch (error) {
      return `${t('messages.lastSeen')} recently`;
    }
  };

  if (!userId) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {!showLabel && !showLastSeen && (
        <div className="relative">
          <div
            className={cn(
              'rounded-full border-2 border-white dark:border-gray-900',
              sizeClasses[size],
              statusColors[presence.status || 'offline'],
              presence.status === 'online' && 'animate-pulse'
            )}
          />
        </div>
      )}
      
      {(showLabel || showLastSeen) && (
        <div className="flex flex-col gap-0.5">
          {showLabel && (
            <div className="flex items-center gap-1.5">
              <div
                className={cn(
                  'rounded-full',
                  'w-2 h-2',
                  statusColors[presence.status || 'offline'],
                  presence.status === 'online' && 'animate-pulse'
                )}
              />
              <span className={cn(
                'font-medium text-xs',
                presence.status === 'online' && 'text-green-600 dark:text-green-400',
                presence.status === 'away' && 'text-yellow-600 dark:text-yellow-400',
                presence.status === 'offline' && 'text-gray-500'
              )}>
                {statusLabels[presence.status || 'offline']}
              </span>
            </div>
          )}
          {showLastSeen && !showLabel && !presence.isOnline && (
            <span className="text-gray-500 dark:text-gray-400 text-xs leading-tight">
              {getLastSeenText()}
            </span>
          )}
          {showLastSeen && !showLabel && presence.isOnline && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                {t('messages.online')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
